import { useState, useEffect, useCallback } from 'react';
import { forwarderRateAPI } from '../../services/api';

/**
 * ForwarderRates — CRUD table for managing forwarder ocean freight rate cards.
 * Follows the same inline-edit pattern as TruckingParameters.
 */
const ForwarderRates = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm());

  function emptyAddForm() {
    return {
      forwarderName: '',
      originPort: '',
      destinationPort: '',
      containerType: '40HC',
      ratePerContainer: '',
      validFrom: '',
      validUntil: '',
      notes: ''
    };
  }

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await forwarderRateAPI.getAll();
      setRates(result.data || []);
    } catch (err) {
      setError('Failed to load forwarder rate cards');
      console.error('ForwarderRates fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const handleEdit = (rate) => {
    setEditingId(rate.id);
    setEditForm({
      forwarderName: rate.forwarderName || '',
      originPort: rate.originPort || '',
      destinationPort: rate.destinationPort || '',
      containerType: rate.containerType || '',
      ratePerContainer: rate.ratePerContainer ?? '',
      validFrom: rate.validFrom ? rate.validFrom.split('T')[0] : '',
      validUntil: rate.validUntil ? rate.validUntil.split('T')[0] : '',
      notes: rate.notes || ''
    });
    setFeedback(null);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setFeedback(null);

      const data = {
        ...editForm,
        ratePerContainer: editForm.ratePerContainer !== '' ? Number(editForm.ratePerContainer) : undefined,
        validFrom: editForm.validFrom || undefined,
        validUntil: editForm.validUntil || undefined
      };

      await forwarderRateAPI.update(editingId, data);
      setFeedback({ type: 'success', message: 'Rate card updated successfully' });
      setEditingId(null);
      setEditForm({});
      await fetchRates();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to update rate card' });
      console.error('ForwarderRates save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddChange = (field, value) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setFeedback(null);

      const data = {
        ...addForm,
        ratePerContainer: addForm.ratePerContainer !== '' ? Number(addForm.ratePerContainer) : undefined,
        validFrom: addForm.validFrom || undefined,
        validUntil: addForm.validUntil || undefined
      };

      await forwarderRateAPI.create(data);
      setFeedback({ type: 'success', message: 'Rate card created successfully' });
      setAddForm(emptyAddForm());
      setShowAddForm(false);
      await fetchRates();
    } catch (err) {
      setFeedback({ type: 'error', message: 'Failed to create rate card' });
      console.error('ForwarderRates add error:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) return <div className="loading">Loading forwarder rate cards...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Forwarder Rate Cards</h1>
        <div className="header-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Cancel' : 'Add Rate Card'}
          </button>
        </div>
      </div>

      {feedback && (
        <div className={feedback.type === 'success' ? 'success-banner' : 'error-message'}
          style={{ marginBottom: '1rem' }}>
          <p>{feedback.message}</p>
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <form onSubmit={handleAddSubmit} className="detail-section" style={{ marginBottom: '1.5rem' }}>
          <h2>New Rate Card</h2>
          <div className="info-grid" style={{ gap: '0.75rem' }}>
            <div className="info-item">
              <label>Forwarder *</label>
              <input className="admin-inline-input" type="text" required
                value={addForm.forwarderName} onChange={(e) => handleAddChange('forwarderName', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Origin Port *</label>
              <input className="admin-inline-input" type="text" required
                value={addForm.originPort} onChange={(e) => handleAddChange('originPort', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Destination Port *</label>
              <input className="admin-inline-input" type="text" required
                value={addForm.destinationPort} onChange={(e) => handleAddChange('destinationPort', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Container Type</label>
              <select className="admin-inline-input"
                value={addForm.containerType} onChange={(e) => handleAddChange('containerType', e.target.value)}>
                <option value="20GP">20GP</option>
                <option value="40GP">40GP</option>
                <option value="40HC">40HC</option>
                <option value="45HC">45HC</option>
              </select>
            </div>
            <div className="info-item">
              <label>Rate / Container ($) *</label>
              <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01" required
                value={addForm.ratePerContainer} onChange={(e) => handleAddChange('ratePerContainer', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Valid From</label>
              <input className="admin-inline-input" type="date"
                value={addForm.validFrom} onChange={(e) => handleAddChange('validFrom', e.target.value)} />
            </div>
            <div className="info-item">
              <label>Valid Until</label>
              <input className="admin-inline-input" type="date"
                value={addForm.validUntil} onChange={(e) => handleAddChange('validUntil', e.target.value)} />
            </div>
            <div className="info-item" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <input className="admin-inline-input" type="text"
                value={addForm.notes} onChange={(e) => handleAddChange('notes', e.target.value)} />
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Create Rate Card'}
            </button>
          </div>
        </form>
      )}

      {rates.length === 0 ? (
        <div className="empty-state">
          <p>No forwarder rate cards configured yet.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="simple-table">
            <thead>
              <tr>
                <th>Forwarder</th>
                <th>Origin Port</th>
                <th>Dest Port</th>
                <th>Container</th>
                <th>Rate / Container</th>
                <th>Valid From</th>
                <th>Valid Until</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((rate) => (
                <tr key={rate.id}>
                  {editingId === rate.id ? (
                    <>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={editForm.forwarderName} onChange={(e) => handleChange('forwarderName', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={editForm.originPort} onChange={(e) => handleChange('originPort', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={editForm.destinationPort} onChange={(e) => handleChange('destinationPort', e.target.value)} />
                      </td>
                      <td>
                        <select className="admin-inline-input"
                          value={editForm.containerType} onChange={(e) => handleChange('containerType', e.target.value)}>
                          <option value="20GP">20GP</option>
                          <option value="40GP">40GP</option>
                          <option value="40HC">40HC</option>
                          <option value="45HC">45HC</option>
                        </select>
                      </td>
                      <td>
                        <input className="admin-inline-input admin-inline-narrow" type="number" step="0.01"
                          value={editForm.ratePerContainer} onChange={(e) => handleChange('ratePerContainer', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="date"
                          value={editForm.validFrom} onChange={(e) => handleChange('validFrom', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="date"
                          value={editForm.validUntil} onChange={(e) => handleChange('validUntil', e.target.value)} />
                      </td>
                      <td>
                        <input className="admin-inline-input" type="text"
                          value={editForm.notes} onChange={(e) => handleChange('notes', e.target.value)} />
                      </td>
                      <td className="actions">
                        <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={saving}>
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={handleCancel} disabled={saving}>
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{rate.forwarderName || '-'}</td>
                      <td>{rate.originPort || '-'}</td>
                      <td>{rate.destinationPort || '-'}</td>
                      <td>{rate.containerType || '-'}</td>
                      <td>{rate.ratePerContainer != null ? `$${Number(rate.ratePerContainer).toFixed(2)}` : '-'}</td>
                      <td>{formatDate(rate.validFrom)}</td>
                      <td>{formatDate(rate.validUntil)}</td>
                      <td className="text-small">{rate.notes || '-'}</td>
                      <td className="actions">
                        <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(rate)}>
                          Edit
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ForwarderRates;
